import { DiwaSigil } from '@/components/DiwaSigil'
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useUI } from '@/store/ui'
import { ApiError } from '@/api/client'
import { DomainConflictError, DomainsAPI, type Domain } from '@/api/domains'
import { GraphAPI } from '@/api/graph'
import { useDomainSelection } from '@/store/domainSelection'
import { LandingAtmosphere } from '@/components/LandingAtmosphere'
import { HelpHint } from '@/components/HelpHint'
import { TalaRefine } from '@/components/TalaRefine'
import { egoKindGloss, mergeTypes, type TalaSuggestion } from '@/lib/talaRefine'
import {
  ONBOARDING_CHOICES,
  draftFromProposed,
  identityFields,
  mergeIdentity,
  proposeDomain,
  type ProposedDomain,
} from '@/lib/onboarding'
import './onboarding.css'

// Tala-guided first-run onboarding (P16-T06). A brand-new keeper lands here — no empty void.
// Tala asks what they want to track, the pick becomes a *proposed* starter domain drawn from
// the T10 category catalog (GET /domains/catalog), and they edit it before committing. Create
// reuses the real POST /domains; a "skip" path drops them on the (guided-empty) Domains page.
// Everything is skippable and non-blocking — power users can be out in two clicks.

/** Re-entry into the guided flow, from anywhere: clears the persisted "skip" flag (so the
 *  first-run gate stops standing aside) and walks back into /onboarding. Both the Domains
 *  empty state ("Start again with Tala") and Settings ("Replay the welcome") use this. */
export function useOnboardingReentry(): () => void {
  const navigate = useNavigate()
  const setOnboardingDismissed = useUI((s) => s.setOnboardingDismissed)
  return () => {
    setOnboardingDismissed(false)
    navigate('/onboarding')
  }
}

/** lowercase_snake a word — mirrors the domain builder's toSnake. */
function toSnake(word: string): string {
  return word
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/** A small editable chip set: tap a chip to remove, type + Enter to add. Used for the proposed
 *  entity / edge types on the review step (snake_case vocabulary words, the default normalize)
 *  and for the "about you" step's expertise/goals, which stay human text (`normalize` = trim). */
function EditableChips({
  chips,
  onChange,
  placeholder,
  normalize = toSnake,
  max,
}: {
  chips: string[]
  onChange: (next: string[]) => void
  placeholder: string
  normalize?: (word: string) => string
  max?: number
}) {
  const [draft, setDraft] = useState('')
  function add() {
    const chip = normalize(draft)
    if (chip && !chips.includes(chip) && (max === undefined || chips.length < max)) {
      onChange([...chips, chip])
    }
    setDraft('')
  }
  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      add()
    }
  }
  return (
    <div className="dwo-chips">
      {chips.map((chip) => (
        <button
          key={chip}
          type="button"
          className="dwo-chip dwo-chip--editable"
          title={`remove ${chip}`}
          onClick={() => onChange(chips.filter((c) => c !== chip))}
        >
          {chip}
          <span aria-hidden="true"> ×</span>
        </button>
      ))}
      <input
        className="dwo-chip-input"
        value={draft}
        placeholder={chips.length === 0 ? placeholder : '+ add'}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
      />
    </div>
  )
}

/** A line Tala "says" — the persona voice for each step. */
function TalaLine({ children }: { children: React.ReactNode }) {
  return (
    <div className="dwo-tala">
      <span className="dwo-tala-mark" aria-hidden="true">✦</span>
      <p className="dwo-tala-text">{children}</p>
    </div>
  )
}

export function Onboarding() {
  const navigate = useNavigate()
  const { diwaUrl, setOnboardingDismissed } = useUI()
  const qc = useQueryClient()
  const select = useDomainSelection((s) => s.select)

  const domainsApi = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const graphApi = useMemo(() => new GraphAPI(diwaUrl), [diwaUrl])
  const { data: catalog } = useQuery({ queryKey: ['domain-catalog'], queryFn: () => domainsApi.catalog() })

  // 'you' comes first: an optional identity-kernel mirror (t0/t1/t3) so Tala knows her keeper.
  const [step, setStep] = useState<'you' | 'ask' | 'review'>('you')
  const [freeText, setFreeText] = useState('')

  // The "about you" step — every field optional, never blocking. On continue, the non-empty
  // tiers are merged onto the EGO entity's attributes.identity (fire-and-forget).
  const [youName, setYouName] = useState('')
  const [youTagline, setYouTagline] = useState('')
  const [youSummary, setYouSummary] = useState('')
  const [youExpertise, setYouExpertise] = useState<string[]>([])
  const [youGoals, setYouGoals] = useState<string[]>([])

  // REPLAY pre-fill: a keeper walking the flow again sees their past answers, not a blank
  // interrogation. The ego node's CURRENT attributes.identity seeds the fields — but only
  // fields still empty, so nothing a keeper already typed this session is clobbered.
  const { data: network } = useQuery({
    queryKey: ['onboarding-ego-identity'],
    queryFn: () => graphApi.network(),
    staleTime: 30_000,
    retry: false, // best-effort — a brand-new account has no graph yet
  })
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current || !network) return
    seeded.current = true
    const ego = network.nodes.find((n) => n.self)
    if (!ego) return
    const past = identityFields(ego.attributes?.identity)
    if (past.name) setYouName((cur) => cur || past.name)
    if (past.tagline) setYouTagline((cur) => cur || past.tagline)
    if (past.summary) setYouSummary((cur) => cur || past.summary)
    if (past.expertise.length > 0) setYouExpertise((cur) => (cur.length > 0 ? cur : past.expertise))
    if (past.goals.length > 0) setYouGoals((cur) => (cur.length > 0 ? cur : past.goals))
  }, [network])

  // The proposed (then keeper-edited) domain lives here once a choice is made.
  const [proposal, setProposal] = useState<ProposedDomain | null>(null)

  const [busy, setBusy] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  function skip() {
    setOnboardingDismissed(true)
    navigate('/domains', { replace: true })
  }

  /** Identity tiers from the "about you" fields — only non-empty tiers are sent, mirroring the
   *  identity-kernel shape: t0 {name, tagline} · t1 {professional_summary, expertise} · t3
   *  {personal_goals}. T4 doctrine: finance/credentials are NEVER asked, so never written. */
  function identityFromFields(): Record<string, unknown> {
    const t0: Record<string, unknown> = {}
    if (youName.trim()) t0.name = youName.trim()
    if (youTagline.trim()) t0.tagline = youTagline.trim()
    const t1: Record<string, unknown> = {}
    if (youSummary.trim()) t1.professional_summary = youSummary.trim()
    if (youExpertise.length > 0) t1.expertise = youExpertise
    const t3: Record<string, unknown> = {}
    if (youGoals.length > 0) t3.personal_goals = youGoals
    const identity: Record<string, unknown> = {}
    if (Object.keys(t0).length > 0) identity.t0 = t0
    if (Object.keys(t1).length > 0) identity.t1 = t1
    if (Object.keys(t3).length > 0) identity.t3 = t3
    return identity
  }

  /** Merge the filled tiers onto the ego entity (the graph node with `self: true`) via
   *  PATCH /graph/entities/{id}. The endpoint merges top-level attribute KEYS but replaces the
   *  `identity` key wholesale — so this is read-modify-write: fetch the current identity, merge
   *  the answered tiers onto it (lib/onboarding.mergeIdentity), send the whole object. A partial
   *  replay (rename only) must never wipe the tiers it didn't touch. Best-effort by design: any
   *  failure (no ego yet, network down) is swallowed — identity capture never blocks onboarding. */
  async function saveIdentity() {
    const updates = identityFromFields()
    if (Object.keys(updates).length === 0) return
    try {
      const net = await graphApi.network() // fresh read at save time, not the prefill snapshot
      const ego = net.nodes.find((n) => n.self)
      if (!ego) return
      const identity = mergeIdentity(ego.attributes?.identity, updates)
      await graphApi.patchEntityAttributes(ego.id, { identity })
    } catch {
      // silently skip — never block onboarding on identity capture
    }
  }

  function continueFromYou() {
    void saveIdentity() // fire-and-forget: the domain step never waits on this
    setStep('ask')
  }

  function pick(choiceKey: string) {
    setProposal(proposeDomain(choiceKey, catalog, freeText))
    setNameError(null)
    setFormError(null)
    setStep('review')
  }

  function patch(next: Partial<ProposedDomain>) {
    setProposal((p) => (p ? { ...p, ...next } : p))
  }

  const canCreate = !!proposal && proposal.name.trim().length > 0 && proposal.entity_types.length > 0

  async function create() {
    if (!proposal || !canCreate || busy) return
    setBusy(true)
    setNameError(null)
    setFormError(null)
    try {
      const domain: Domain = await domainsApi.create(draftFromProposed(proposal))
      await qc.invalidateQueries({ queryKey: ['domains'] })
      select(domain) // land them "in it" — the control plane reflects the new region
      setOnboardingDismissed(true)
      navigate('/domains', { replace: true })
    } catch (err) {
      if (err instanceof DomainConflictError) setNameError(err.detail)
      else if (err instanceof ApiError && err.status === 402)
        setFormError(err.detail ?? 'Your plan is at its domain limit.')
      else setFormError("Couldn't create that domain — try again, or skip and do it yourself.")
    } finally {
      setBusy(false)
    }
  }

  /** Merge applied Tala suggestions into the proposal — dedupe + snake-normalize, and ego
   *  suggestions carry the kind Tala marked, so the keeper never picks event/state raw. */
  function applySuggestions(suggestions: TalaSuggestion[]) {
    setProposal((p) => {
      if (!p) return p
      const ents = suggestions.filter((s) => s.kind === 'entity').map((s) => s.name)
      const edges = suggestions.filter((s) => s.kind === 'edge').map((s) => s.name)
      const have = new Set(p.ego_relations.map((e) => e.name))
      const egos = suggestions.filter((s) => s.kind === 'ego' && !have.has(s.name))
      return {
        ...p,
        entity_types: mergeTypes(p.entity_types, ents),
        relation_types: mergeTypes(p.relation_types, edges),
        ego_relations:
          egos.length > 0
            ? [...p.ego_relations, ...egos.map((e) => ({ name: e.name, kind: e.egoKind }))]
            : p.ego_relations,
      }
    })
  }

  return (
    <div className="dwo">
      <LandingAtmosphere />
      <main className="dwo-stage">
        <div className="dwo-card">
          <div className="dwo-head">
            <DiwaSigil size={44} className="dwo-sigil" />
            <div>
              <div className="dwo-kicker">Welcome to Diwa</div>
              <h1 className="dwo-title">
                {step === 'you' ? 'Tell Tala about yourself' : "Let's shape your first domain"}
              </h1>
            </div>
          </div>

          {step === 'you' && (
            <>
              <TalaLine>
                I'm Tala, your familiar. Before we shape your world — a little about <em>you</em>, so I
                know who I serve. Everything here is optional; skip anything, or all of it.
              </TalaLine>

              <div className="dwo-field">
                <label className="dwo-field-label" htmlFor="dwo-you-name">Your name <span className="dwo-opt">optional</span></label>
                <input
                  id="dwo-you-name"
                  className="dwo-input"
                  value={youName}
                  placeholder="What should I call you?"
                  onChange={(e) => setYouName(e.target.value)}
                />
              </div>

              <div className="dwo-field">
                <label className="dwo-field-label" htmlFor="dwo-you-tagline">What you do <span className="dwo-opt">one line</span></label>
                <input
                  id="dwo-you-tagline"
                  className="dwo-input"
                  value={youTagline}
                  placeholder="e.g. founder · investor · researcher"
                  onChange={(e) => setYouTagline(e.target.value)}
                />
              </div>

              <div className="dwo-field">
                <label className="dwo-field-label" htmlFor="dwo-you-summary">Your work, in a sentence <span className="dwo-opt">optional</span></label>
                <textarea
                  id="dwo-you-summary"
                  className="dwo-input dwo-textarea"
                  value={youSummary}
                  placeholder="e.g. I run a two-person studio building tools for analysts."
                  onChange={(e) => setYouSummary(e.target.value)}
                />
              </div>

              <div className="dwo-field">
                <div className="dwo-field-label">Expertise <span className="dwo-opt">up to 5</span></div>
                <EditableChips
                  chips={youExpertise}
                  onChange={setYouExpertise}
                  placeholder="e.g. capital markets, Python…"
                  normalize={(w) => w.trim()}
                  max={5}
                />
              </div>

              <div className="dwo-field">
                <div className="dwo-field-label">What are you building toward? <span className="dwo-opt">optional</span></div>
                <EditableChips
                  chips={youGoals}
                  onChange={setYouGoals}
                  placeholder="e.g. raise a seed round, ship v1…"
                  normalize={(w) => w.trim()}
                />
              </div>

              <div className="dwo-hint">Tala never asks for financial or credential details.</div>

              <div className="dwo-actions">
                <button type="button" className="dwo-btn" onClick={continueFromYou}>
                  Continue →
                </button>
                <button type="button" className="dwo-skip dwo-skip--inline" onClick={() => setStep('ask')}>
                  Skip — Tala can learn later
                </button>
              </div>
            </>
          )}

          {step === 'ask' && (
            <>
              <TalaLine>
                A <em>domain</em> is one part of your world — a set of things you care about and how
                they connect. What do you want to track first?
              </TalaLine>

              <div className="dwo-choices">
                {ONBOARDING_CHOICES.map((c) => (
                  <button key={c.key} type="button" className="dwo-choice" onClick={() => pick(c.key)}>
                    <span className="dwo-choice-label">{c.label}</span>
                    <span className="dwo-choice-blurb">{c.blurb}</span>
                  </button>
                ))}
              </div>

              <div className="dwo-free">
                <label className="dwo-free-label" htmlFor="dwo-free-input">
                  …or tell me in your own words (optional)
                </label>
                <input
                  id="dwo-free-input"
                  className="dwo-input"
                  value={freeText}
                  placeholder="e.g. the studios and creators I follow"
                  onChange={(e) => setFreeText(e.target.value)}
                  onKeyDown={(e) => {
                    // Free text alone still needs a shape — send it through the custom canvas.
                    if (e.key === 'Enter' && freeText.trim()) pick('custom')
                  }}
                />
                {freeText.trim() && (
                  <button type="button" className="dwo-btn dwo-btn--ghost" onClick={() => pick('custom')}>
                    Start from that →
                  </button>
                )}
              </div>

              <button type="button" className="dwo-skip" onClick={skip}>
                Skip — I'll set it up myself
              </button>
            </>
          )}

          {step === 'review' && proposal && (
            <>
              <TalaLine>
                Here's a starter{proposal.categoryKey ? '' : ' canvas'} — rename it, add or drop types, then
                create it. Nothing's saved until you say so.
              </TalaLine>

              <div className="dwo-field">
                <label className="dwo-field-label" htmlFor="dwo-name">Name</label>
                <input
                  id="dwo-name"
                  className="dwo-input"
                  value={proposal.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="Name your domain"
                />
                {nameError && <div className="dwo-error">{nameError}</div>}
              </div>

              <div className="dwo-field">
                <label className="dwo-field-label" htmlFor="dwo-desc">Description <span className="dwo-opt">optional</span></label>
                <textarea
                  id="dwo-desc"
                  className="dwo-input dwo-textarea"
                  value={proposal.description}
                  onChange={(e) => patch({ description: e.target.value })}
                  placeholder="What this domain of your world holds."
                />
              </div>

              <div className="dwo-field">
                <div className="dwo-field-label">Entity types <span className="dwo-opt">the nouns you'll track</span></div>
                <EditableChips
                  chips={proposal.entity_types}
                  onChange={(v) => patch({ entity_types: v })}
                  placeholder="add at least one"
                />
                {proposal.entity_types.length === 0 && <div className="dwo-hint">at least one entity type to create</div>}
              </div>

              <div className="dwo-field">
                <div className="dwo-field-label">Edge types <span className="dwo-opt">how they relate</span></div>
                <EditableChips
                  chips={proposal.relation_types}
                  onChange={(v) => patch({ relation_types: v })}
                  placeholder="entity ↔ entity edges"
                />
              </div>

              {proposal.ego_relations.length > 0 && (
                <div className="dwo-field">
                  <div className="dwo-field-label">
                    Your relations <span className="dwo-opt">what this domain records about you</span>
                  </div>
                  <div className="dwo-chips" style={{ background: 'transparent', border: 'none', padding: 0 }}>
                    {proposal.ego_relations.map((e) => (
                      <HelpHint key={e.name} gloss={egoKindGloss(e.kind)}>
                        <button
                          type="button"
                          className="dwo-chip dwo-chip--editable"
                          title={`remove ${e.name}`}
                          onClick={() =>
                            patch({ ego_relations: proposal.ego_relations.filter((x) => x.name !== e.name) })
                          }
                        >
                          {e.name} · {e.kind}
                          <span aria-hidden="true"> ×</span>
                        </button>
                      </HelpHint>
                    ))}
                  </div>
                </div>
              )}

              {proposal.suggestedSpring && (
                <div className="dwo-spring">
                  Once it exists, you can feed it with the <strong>{proposal.suggestedSpring}</strong> spring —
                  attach it from the domain's panel.
                </div>
              )}

              <TalaRefine
                surface="onboarding"
                domainName={proposal.name}
                entityTypes={proposal.entity_types}
                relationTypes={proposal.relation_types}
                egoNames={proposal.ego_relations.map((e) => e.name)}
                onApply={applySuggestions}
                buttonClassName="dwo-btn dwo-btn--ghost dwo-tala-ask"
                replyClassName="dwo-tala-reply"
              />

              {formError && <div className="dwo-error">{formError}</div>}

              <div className="dwo-actions">
                <button type="button" className="dwo-btn" disabled={!canCreate || busy} onClick={create}>
                  {busy ? 'Creating…' : 'Create domain'}
                </button>
                <button type="button" className="dwo-btn dwo-btn--ghost" disabled={busy} onClick={() => setStep('ask')}>
                  ← Back
                </button>
                <button type="button" className="dwo-skip dwo-skip--inline" disabled={busy} onClick={skip}>
                  Skip
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
